import { useMemo, useRef } from 'react';
import { useCheckLoadedAndShow } from '../hooks/useCheckLoadedAndShow';
import { postParentWindowMessage } from '../../../../utils/post-message';
import { useMessageListener } from '../hooks/useMessageListener';
// After the login route is hit, how long we wait to gather received cookies
// before sending them off to Newton (see comments in `useWorkday` for more
// details).
var POST_LOGIN_TIMEOUT_DURATION_MS = 6000;
// Context: https://getpinwheel.slack.com/archives/C06GT5KDRJQ
// This integration was created to support FNB (a workday platform) on iOS with
// the requirement of it working on at least the 2.4.2 iOS SDK version since
// that's the lowest edge native version in use by our customers (as of 2/3/24).
export function useWorkday() {
    // Here, `useRef` is used (as opposed to `useState`) so that updates happen
    // immediately/synchronously. If `useState` is used, then it's possible for us
    // to "miss" events due to React re-renders being asynchronous. E.g. scenario:
    // 1. We receive a `window_location_load_start` event matching the post-login
    //    route, and call `setLoggedIn(true)`. The update is scheduled for the
    //    next React render cycle.
    // 2. We receive a `window_location_load_complete` event which contains the
    //    relevant cookies that we need. However, `isLoggedIn` is still `false`
    //    since the next render hasn't happened yet, and thus we ignore the event.
    // 3. The React re-render happens, but at this point, we've already "missed"
    //    the relevant event.
    var isLoggedInRef = useRef(false);
    var setosMap = useRef({});
    var eventHandlerDown = useMemo(function () {
        return function (_a) {
            var _b;
            var data = _a.data;
            if ((data === null || data === void 0 ? void 0 : data.type) !== 'PINWHEEL_INTERNAL_COMM_DOWN')
                return;
            // Detect when we have logged in. On version 2.4.2 of the iOS SDK, the
            // `window_location_load_start` event doesn't contain cookies in the
            // payload, whereas the `location load complete` only contains the cookies
            // but doesn't contain the location. Thus, we only start checking the load
            // complete events from this point onwards.
            if (data.eventName === 'window_location_load_start') {
                // Note: the dot is important -- for UPS, there is a `login-saml2` path
                // that is reached on the initial login page.
                if ((_b = data.payload.location) === null || _b === void 0 ? void 0 : _b.match(/login-saml\./)) {
                    isLoggedInRef.current = true;
                }
            }
            else if (data.eventName === 'window_location_load_complete') {
                if (!isLoggedInRef.current)
                    return;
                var payload = data.payload;
                var setos = payload.setos;
                // While we built/tested this integration on Cafe Royal (CircleK), we
                // found that once `isLoggedInRef` is set, there were still multiple
                // `window_location_load_complete` events that we receive with different
                // cookies, and the integration would only work with the cookies from a
                // specific event (but there isn't a good programmatic way to detect
                // when we've received that specific one). Thus, as a hack, we are
                // waiting for 5 seconds from when `isLoggedInRef` is set and storing
                // all the cookies seen in incoming `window_location_load_complete`
                // events (overwriting with newer values when applicable). After the
                // timeout, we send the entire combined cookies payload to Newton.
                setos === null || setos === void 0 ? void 0 : setos.forEach(function (seto) {
                    // Key off the domain too in case the same seto name is present on
                    // multiple domains.
                    setosMap.current["".concat(seto.name, " ").concat(seto.domain)] = seto;
                });
                // During the post-login timeout duration, hide the edge native webview
                // so that Link shows a loading screen, as otherwise we would be showing
                // the logged in homepage for a few seconds before it goes away (which
                // is a bad and confusing UX).
                postParentWindowMessage({
                    type: 'PINWHEEL_INTERNAL_COMM_UP',
                    eventName: 'browser_visibility',
                    payload: { visible: false },
                });
                setTimeout(function () {
                    // HACK: triggers the event handler logic in <TemplateEdgeNative> by
                    // mimicking an event received from the SDK. Note that we have to mock
                    // this instead of sending a `setos_request` event because that isn't
                    // supported in v2.4.2 of the iOS SDK.
                    var mockMessage = {
                        type: 'PINWHEEL_INTERNAL_COMM_DOWN',
                        eventName: 'setos_response',
                        payload: {
                            setos: Object.values(setosMap.current),
                            // HACK: technically this field should be an actual complete URL.
                            // However, since this hook is used across multiple workday
                            // platforms, we don't know what the URL would be, and hardcoding
                            // it as just the subpath portion the success criteria still
                            // results in the integration succeeding.
                            location: 'home.htmld',
                        },
                    };
                    window.postMessage(mockMessage, window.origin);
                }, POST_LOGIN_TIMEOUT_DURATION_MS);
            }
        };
    }, []);
    useMessageListener(eventHandlerDown);
    useCheckLoadedAndShow();
}
